import { useState } from "react";
import api from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { useRefresh } from "@/hooks/useRefresh";
import { peso, num, fmtDate } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Wallet, AlertTriangle, PackageX, ShoppingCart, Trash2, ArrowDown, ArrowUp } from "lucide-react";

const COLORS = ["#002FA7", "#15803D", "#F59E0B", "#B91C1C", "#7C3AED", "#0891B2"];
const CHART_HEIGHT = 220;
const AXIS_TICK = { fontSize: 10 };
const Y_AXIS_WIDTH = 45;
const LEGEND_STYLE = { fontSize: 11 };
const DONUT_INNER_RADIUS = 50;
const DONUT_OUTER_RADIUS = 80;
const MOVE_BADGE = {
  receive: "bg-green-100 text-green-800", consume: "bg-slate-100 text-slate-700",
  adjust: "bg-blue-100 text-blue-800", wastage: "bg-red-100 text-red-700",
  transfer_out: "bg-amber-100 text-amber-800", transfer_in: "bg-emerald-100 text-emerald-800",
};

export default function Dashboard() {
  const { selected } = useBranch();
  const [d, setD] = useState(null);

  useRefresh(() => {
    api.get("/dashboard", { params: { branch_id: selected } }).then((r) => setD(r.data));
  }, [selected]);

  if (!d) return <p className="text-sm text-muted-foreground">Loading dashboard…</p>;

  const kpis = [
    { label: "Inventory value", value: peso(d.total_value), icon: Wallet, id: "kpi-inventory-value" },
    { label: "Low stock", value: num(d.low_stock_count), icon: AlertTriangle, id: "kpi-low-stock", accent: d.low_stock_count > 0 && "text-amber-600" },
    { label: "Out of stock", value: num(d.out_of_stock_count), icon: PackageX, id: "kpi-out-of-stock", accent: d.out_of_stock_count > 0 && "text-red-600" },
    { label: "Open POs", value: num(d.open_pos), icon: ShoppingCart, id: "kpi-open-pos" },
    { label: "Wastage this month", value: peso(d.wastage_month), icon: Trash2, id: "kpi-wastage-month", accent: "text-red-600" },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {kpis.map((k) => (
          <div key={k.id} className="border rounded-xl bg-card p-4" data-testid={k.id}>
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
              <k.icon className="h-3.5 w-3.5" /> {k.label}
            </div>
            <div className={`font-heading text-xl sm:text-2xl font-extrabold ${k.accent || ""}`}>{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="border rounded-xl bg-card overflow-hidden" data-testid="low-stock-table">
            <div className="px-4 py-3 border-b font-semibold text-sm">Low & out-of-stock items</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground text-left">
                  <tr className="border-b">
                    <th className="py-2 px-3">Item</th><th className="py-2 px-3">Branch</th>
                    <th className="py-2 px-3 text-right">On hand</th><th className="py-2 px-3 text-right">Reorder pt</th>
                    <th className="py-2 px-3 text-right">Gap</th><th className="py-2 px-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {d.low_stock.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">All stocked up 🎉</td></tr>}
                  {d.low_stock.map((r) => (
                    <tr key={r.stock_id} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2 px-3 font-medium">{r.item_name}</td>
                      <td className="py-2 px-3 text-muted-foreground">{r.branch_name}</td>
                      <td className="py-2 px-3 text-right">{num(r.qty)} {r.unit}</td>
                      <td className="py-2 px-3 text-right">{num(r.reorder_point)}</td>
                      <td className="py-2 px-3 text-right text-red-600 font-medium">{num(r.gap)}</td>
                      <td className="py-2 px-3">
                        <Badge className={r.status === "out" ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}>
                          {r.status === "out" ? "Out" : "Low"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="border rounded-xl bg-card p-4" data-testid="value-by-branch-chart">
              <div className="font-semibold text-sm mb-3">Inventory value by branch</div>
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart data={d.value_by_branch}>
                  <XAxis dataKey="branch" tick={AXIS_TICK} interval={0} tickFormatter={(v) => v.split(" - ")[0]} />
                  <YAxis tick={AXIS_TICK} tickFormatter={(v) => `₱${(v / 1000).toFixed(0)}k`} width={Y_AXIS_WIDTH} />
                  <Tooltip formatter={(v) => peso(v)} />
                  <Bar dataKey="value" fill="#002FA7" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="border rounded-xl bg-card p-4" data-testid="top-wastage-chart">
              <div className="font-semibold text-sm mb-3">Top wastage this month</div>
              {d.top_wastage.length === 0 ? (
                <p className="text-sm text-muted-foreground py-16 text-center">No wastage this month</p>
              ) : (
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <PieChart>
                    <Pie data={d.top_wastage} dataKey="value" nameKey="item" innerRadius={DONUT_INNER_RADIUS} outerRadius={DONUT_OUTER_RADIUS}>
                      {d.top_wastage.map((entry, i) => <Cell key={entry.item} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => peso(v)} />
                    <Legend wrapperStyle={LEGEND_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <div className="border rounded-xl bg-card overflow-hidden h-fit" data-testid="recent-movements-feed">
          <div className="px-4 py-3 border-b font-semibold text-sm">Recent movements</div>
          <ul className="divide-y">
            {d.recent_movements.map((m) => (
              <li key={m.movement_id} className="px-4 py-2.5 flex items-start gap-3 hover:bg-slate-50">
                <span className={`mt-0.5 h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${m.qty >= 0 ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                  {m.qty >= 0 ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{m.item_name}</div>
                  <div className="text-xs text-muted-foreground">{m.branch_name} · {m.user_name} · {fmtDate(m.created_at)}</div>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${MOVE_BADGE[m.type]}`}>{m.type.replace("_", " ")}</span>
                  <div className="text-xs font-medium mt-0.5">{m.qty > 0 ? "+" : ""}{num(m.qty)} {m.unit}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
